import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  Chip,
  Paper,
} from '@mui/material';
import {
  VerifiedUser,
  LocationOn,
  Store,
  QrCode,
  Discount,
  TrendingUp,
  Verified,
  People,
  Handshake,
  Analytics,
  Support,
  Star,
  ArrowForward,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Typewriter from 'typewriter-effect';
import videoBackground from '../assets/videos/home_page/Video_Related_to_Shopping.mp4';
import BackToTop from '../components/BackToTop';
import SEO from '../components/SEO';

const Home = () => {
  const businessBenefits = [
    {
      icon: <People sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Access to 10D Members',
      description: 'Connect with thousands of verified 10D members actively looking for local businesses',
      highlight: '50K+ Active Members',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Increased Foot Traffic',
      description: 'Drive more customers to your physical store with exclusive member offers',
      highlight: '25% Average Sales Increase',
    },
    {
      icon: <QrCode sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Simple Verification System',
      description: 'Easy QR code scanning to verify 10D membership and apply discounts',
      highlight: 'Zero Setup Cost',
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Customer Insights',
      description: 'Get data on 10D member visits, discount usage, and customer behavior',
      highlight: 'Real-time Analytics',
    },
    {
      icon: <Handshake sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'No Partnership Fees',
      description: 'Join 10D completely free - no hidden charges or commission fees',
      highlight: '100% Free to Join',
    },
    {
      icon: <Support sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Dedicated Support',
      description: '24/7 support to help you maximize your 10D partnership success',
      highlight: '24/7 Support',
    },
  ];

  const userBenefits = [
    {
      icon: <Discount sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Exclusive Member Discounts',
      description: 'Enjoy special pricing and offers only available to verified 10D members',
      highlight: 'Up to 30% Off',
    },
    {
      icon: <Verified sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Verified Business Partners',
      description: 'All businesses are verified and trusted partners of 10D Stores',
      highlight: '1000+ Verified Partners',
    },
    {
      icon: <Store sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'In-Store Shopping Experience',
      description: 'Visit physical stores to see, touch, and experience products firsthand',
      highlight: 'Real Product Experience',
    },
    {
      icon: <QrCode sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Simple QR Verification',
      description: 'Just scan your QR code to verify membership and unlock discounts',
      highlight: 'One-Click Verification',
    },
    {
      icon: <LocationOn sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Discover Local Gems',
      description: 'Find amazing local businesses in your area you never knew existed',
      highlight: 'Hidden Local Treasures',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Save Money & Support Local',
      description: 'Reduce costs while helping local businesses thrive in your community',
      highlight: 'Win-Win for Everyone',
    },
  ];



  const howItWorks = [
    {
      step: '1',
      title: 'Subscribe to 10D',
      description: 'Choose your subscription plan and become a 10D member',
      icon: <VerifiedUser sx={{ fontSize: 30, color: 'primary.main' }} />,
    },
    {
      step: '2',
      title: 'Discover Local Businesses',
      description: 'Browse verified 10D partner businesses in your area',
      icon: <LocationOn sx={{ fontSize: 30, color: 'primary.main' }} />,
    },
    {
      step: '3',
      title: 'Visit the Store',
      description: 'Go to the physical store to see and experience products',
      icon: <Store sx={{ fontSize: 30, color: 'primary.main' }} />,
    },
    {
      step: '4',
      title: 'Scan & Save',
      description: 'Scan your 10D QR code to verify membership and get discounts',
      icon: <QrCode sx={{ fontSize: 30, color: 'primary.main' }} />,
    },
  ];

  return (
    <Box>
      <SEO
        title="Home"
        description="Join 10D Stores to discover local businesses, get exclusive discounts, and save money on every purchase."
        canonical="https://10dstores.in/"
      />
      {/* Hero Section with Video Background */}
      <Box
        sx={{
          position: 'relative',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          color: 'white',
        }}
      >
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: -2,
          }}
        >
          <source src={videoBackground} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Dark Overlay for better text readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: -1,
          }}
        />

        {/* Content - Left Aligned */}
        <Container maxWidth="lg" sx={{ zIndex: 1 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100vh',
            maxWidth: '600px',
          }}>

            {/* Main Headline */}
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 500,
                fontSize: '4.5rem',
                lineHeight: 1.1,
                mb: 3,
                color: '#FFFFFF',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              }}
            >
              Welcome to the 10D Stores.
            </Typography>

            {/* Subtext with Typewriter Effect */}
            <Typography
              component="p"
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontSize: '1.5rem',
                fontWeight: 400,
                lineHeight: 1.4,
                mb: 4,
                color: '#FFFFFF',
                maxWidth: '550px',
                textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                minHeight: '3rem', // Maintain consistent height during animation
              }}
            >
              The Platform That{' '}
              <Typewriter
                onInit={(typewriter) => {
                  typewriter
                    .pauseFor(2000)
                    .typeString('Finds You Unbeatable Local Deals.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Saves You Money on Every Purchase.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Connects You to Your Community\'s Best.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Makes Supporting Local Easy.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Delivers Exclusive Offers Daily.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Showcases Curated Local Businesses.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Powers Smart Local Shopping.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Aggregates the Best Nearby Discounts.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Bridges the Gap Between Shops and Shoppers.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Is Redefining the Local Marketplace.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Strengthens Local Economies.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Empowers Neighborhood Businesses.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Builds a Stronger Community, One Deal at a Time.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Celebrates the Heart of Your City.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(500)
                    .typeString('Champions the Local Entrepreneur.')
                    .pauseFor(3000)
                    .deleteAll()
                    .pauseFor(1000)
                    .start();
                }}
                options={{
                  loop: true,
                  delay: 50,
                  cursor: '|',
                  cursorClassName: 'typewriter-cursor',
                }}
              />
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/register"
                className="cta-button"
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  backgroundColor: '#FFFFFF',
                  color: '#000000',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  padding: '1rem 2rem',
                  borderRadius: '9999px',
                  border: 'none',
                  transition: 'all 0.2s ease-in-out',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    transform: 'scale(1.05)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                  },
                }}
              >
                Join 10D Today
              </Button>
              <Button
                component={Link}
                to="/business"
                className="cta-button"
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  backgroundColor: '#FFFFFF',
                  color: '#000000',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  padding: '1rem 2rem',
                  borderRadius: '9999px',
                  border: 'none',
                  transition: 'all 0.2s ease-in-out',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    transform: 'scale(1.05)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                  },
                }}
              >
                Partner with 10D
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Why Businesses Should Partner Section */}
      <Box sx={{ py: 8, backgroundColor: '#F8FAFC' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B', mb: 3 }}>
              Why Businesses Should Partner with 10D
            </Typography>
            <Typography variant="h6" component="h3" sx={{ color: '#475569', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6 }}>
              Join hundreds of businesses already thriving with 10D partnerships.
              Our platform is designed to drive real results for your business.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {businessBenefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      transition: 'transform 0.3s ease',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                    },
                  }}
                  elevation={2}
                >
                  <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {benefit.description}
                  </Typography>
                  <Chip
                    label={benefit.highlight}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              component={Link}
              to="/business"
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              Learn More About Partnership
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Why Users Should Subscribe Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B', mb: 3 }}>
              Why Users Should Subscribe to 10D
            </Typography>
            <Typography variant="h6" component="h3" sx={{ color: '#475569', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6 }}>
              Become a 10D member and unlock exclusive access to local business discounts.
              It's not just about discovering amazing local experiences.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {userBenefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      transition: 'transform 0.3s ease',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                    },
                  }}
                  elevation={2}
                >
                  <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {benefit.description}
                  </Typography>
                  <Chip
                    label={benefit.highlight}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              Get Started with 10D
            </Button>
          </Box>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ backgroundColor: '#F8FAFC', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#1E293B' }}>
            How 10D Works
          </Typography>
          <Grid container spacing={4}>
            {howItWorks.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' },
                  }}
                  elevation={2}
                >
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={step.step}
                      color="primary"
                      sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 2 }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>{step.icon}</Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B', mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', fontSize: '1rem', lineHeight: 1.6 }}>
                    {step.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>



      {/* Key Benefits Section */}
      <Box sx={{ backgroundColor: '#F8FAFC', py: 8, color: 'text.primary' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B', mb: 4 }}>
              Key Benefits of 10D Membership
            </Typography>
            <Grid container spacing={4} sx={{ mt: 4 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 4, backgroundColor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', height: '100%' }}>
                  <Star sx={{ fontSize: 60, mb: 2, color: '#2563EB' }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B', mb: 2 }}>
                    💰 Save Money
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', fontSize: '1rem', lineHeight: 1.6 }}>
                    Exclusive discounts at verified local businesses
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 4, backgroundColor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', height: '100%' }}>
                  <Star sx={{ fontSize: 60, mb: 2, color: '#2563EB' }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B', mb: 2 }}>
                    🏪 Support Local
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', fontSize: '1rem', lineHeight: 1.6 }}>
                    Help local businesses thrive in your community
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 4, backgroundColor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', height: '100%' }}>
                  <Star sx={{ fontSize: 60, mb: 2, color: '#2563EB' }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B', mb: 2 }}>
                    🔍 Discover More
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', fontSize: '1rem', lineHeight: 1.6 }}>
                    Find hidden gems and new businesses in your area
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f17a4a 0%, #f8a17a 100%)',
              color: 'white',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(241, 122, 74, 0.3)'



            }}
          >
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3, color: 'white' }}>
              Ready to Start Your 10D Journey?
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, fontSize: '1.25rem', lineHeight: 1.6, color: 'white' }}>
              Join thousands of 10D members already enjoying exclusive discounts at local businesses,
              or partner with us to grow your business.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/customers"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: '#5a9b9a',
                  color: 'white',
                  border: '2px solid #e6b8a2',
                  '&:hover': {
                    backgroundColor: '#e3662d',
                    borderColor: '#f5f5f5',
                    transform: 'translateY(-2px)'
                  },
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(90, 155, 154, 0.3), 0 2px 8px rgba(230, 184, 162, 0.2)',
                  transition: 'all 0.3s ease',
                }}
              >
                Join 10D Today
              </Button>
              <Button
                component={Link}
                to="/business"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: '#5a9b9a',
                  color: '#5a9b9a',
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: '#e3662d',
                    backgroundColor: 'rgba(227, 102, 45, 0.1)',
                    transform: 'translateY(-2px)'
                  },
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                }}
              >
                Partner with 10D
              </Button>
            </Box>
          </Paper>
        </Container >
      </Box >


      <BackToTop />
    </Box >
  );
};

export default Home;
